/*
 * Copyright (C) 2021 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Root Canal Bluetooth Protocol Utilities for Cuttlefish
 *
 * Provides message encoding and decoding for the Root Canal Bluetooth protocol
 */

const utf8Encoder = new TextEncoder()
const utf8Decoder = new TextDecoder()

/**
 * Calculate the size of a Root Canal message
 */
function calculateMessageSize(name: string, args: string[]): number {
  let result = 0

  result += 1 + name.length // length of name + its data
  result += 1 // count of args

  for (let i = 0; i < args.length; i++) {
    result += 1 // length of args[i]
    result += (args[i] ?? '').length // data of args[i]
  }

  return result
}

/**
 * Add a single byte to the array
 */
function addU8(array: Uint8Array, pos: number, val: number): number {
  array[pos] = val & 0xff
  return pos + 1
}

/**
 * Add a payload (byte array) to the array
 */
function addPayload(array: Uint8Array, pos: number, payload: Uint8Array): number {
  array.set(payload, pos)
  return pos + payload.length
}

/**
 * Add a string to the array (length-prefixed)
 */
function addString(array: Uint8Array, pos: number, val: string): number {
  let curPos = pos

  curPos = addU8(array, curPos, val.length)
  return addPayload(array, curPos, utf8Encoder.encode(val))
}

/**
 * Create a Root Canal message
 *
 * @param command - Command name
 * @param args - Command arguments
 * @returns Encoded message as Uint8Array
 */
export function createRootcanalMessage(command: string, args: string[]): Uint8Array {
  const messageSize = calculateMessageSize(command, args)
  const arrayBuffer = new ArrayBuffer(messageSize)
  const array = new Uint8Array(arrayBuffer)
  let pos = 0

  pos = addString(array, pos, command)
  pos = addU8(array, pos, args.length)

  for (let i = 0; i < args.length; i++) {
    pos = addString(array, pos, args[i] ?? '')
  }

  return array
}

/**
 * Decode a Root Canal message
 *
 * @param array - Encoded message
 * @returns Decoded message string
 */
export function decodeRootcanalMessage(array: Uint8Array): string {
  const size = array[0] ?? 0
  const message = array.slice(1)

  return utf8Decoder.decode(message)
}

/**
 * Root Canal Command Builder
 * Provides a fluent API for building Root Canal commands
 */
export class RootcanalCommandBuilder {
  private command: string
  private args: string[] = []

  constructor(command: string) {
    this.command = command
  }

  /**
   * Add an argument to the command
   */
  arg(value: string): this {
    this.args.push(value)
    return this
  }

  /**
   * Add multiple arguments to the command
   */
  addArgs(...values: string[]): this {
    this.args.push(...values)
    return this
  }

  /**
   * Build the command message
   */
  build(): Uint8Array {
    return createRootcanalMessage(this.command, this.args)
  }
}

/**
 * Create a Root Canal command builder
 */
export function rootcanalCommand(command: string): RootcanalCommandBuilder {
  return new RootcanalCommandBuilder(command)
}
